import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import { Microphone, ArrowClockwise, PaperPlaneTilt, Scales, Sparkle, Spinner, Warning } from '@phosphor-icons/react';
import { NarrationPlayer } from '../narration/NarrationPlayer';
import { NarrationVersionList } from '../narration/NarrationVersionList';

const POLL_MS = 4000;

export const NarrationTab = ({ caseId }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [feedback, setFeedback] = useState('');
  const [generating, setGenerating] = useState(false);
  const pollRef = useRef(null);
  const urlRef = useRef('');
  const autoStale = useRef(false);

  const setAudio = useCallback((url) => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = url;
    setAudioUrl(url);
  }, []);

  const loadStatus = useCallback(async () => {
    const { data } = await api.get(`/cases/${caseId}/narration`);
    setStatus(data);
    return data;
  }, [caseId]);

  const loadNarration = useCallback(async (nid) => {
    const { data } = await api.get(`/cases/${caseId}/narration/${nid}`);
    setActive(data.narration);
    if (data.narration?.status === 'ready') {
      const resp = await api.get(`/cases/${caseId}/narration/${nid}/audio`, { responseType: 'blob' });
      setAudio(URL.createObjectURL(resp.data));
    }
  }, [caseId, setAudio]);

  const pollUntilReady = useCallback((nid) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/cases/${caseId}/narration/${nid}`);
        const st = data.narration?.status;
        if (st === 'ready') {
          clearInterval(pollRef.current);
          setGenerating(false);
          await loadNarration(nid);
          await loadStatus();
        } else if (st === 'error') {
          clearInterval(pollRef.current);
          setGenerating(false);
          setActive(data.narration);
          await loadStatus();
        }
      } catch (err) {
        logger.debug('narration poll transient error:', err);
      }
    }, POLL_MS);
  }, [caseId, loadNarration, loadStatus]);

  const generate = useCallback(async (opts = {}) => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/narration/generate`, {
        feedback: opts.feedback || '', trigger: opts.trigger || (opts.feedback ? 'feedback' : 'manual'),
      });
      setFeedback('');
      await loadStatus();
      pollUntilReady(data.narration_id);
    } catch (err) {
      setGenerating(false);
      alert(err.response?.data?.detail || 'Failed to start narration');
    }
  }, [caseId, loadStatus, pollUntilReady]);

  const init = useCallback(async () => {
    setLoading(true);
    try {
      const data = await loadStatus();
      const latest = data.latest;
      if (latest?.status === 'generating') {
        setGenerating(true);
        pollUntilReady(latest._id);
      } else if (latest?.status === 'ready') {
        await loadNarration(latest._id);
        if (data.is_stale && !autoStale.current) {
          autoStale.current = true;
          generate({ trigger: 'material_change' });
        }
      }
    } catch (err) {
      logger.error('narration init failed:', err);
    } finally {
      setLoading(false);
    }
  }, [loadStatus, loadNarration, pollUntilReady, generate]);

  useEffect(() => {
    init();
    return () => { clearInterval(pollRef.current); if (urlRef.current) URL.revokeObjectURL(urlRef.current); };
  }, [init]);

  const deleteVersion = async (nid) => {
    if (!window.confirm('Delete this narration version?')) return;
    await api.delete(`/cases/${caseId}/narration/${nid}`);
    const data = await loadStatus();
    if (active?._id === nid) {
      setActive(null); setAudio('');
      if (data.latest?.status === 'ready') loadNarration(data.latest._id);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20" data-testid="narration-tab"><Spinner size={28} className="animate-spin text-[#002FA7]" /></div>;
  }

  const hasAny = status?.has_any;
  const isStale = status?.is_stale;
  const errored = active?.status === 'error';
  const showGeneratingState = generating && (!active || active.status !== 'ready');

  return (
    <div data-testid="narration-tab" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-outfit text-lg font-semibold text-[#09090B] flex items-center gap-2">
            <Scales size={20} className="text-[#002FA7]" weight="fill" /> AI Legal Narration
          </h3>
          <p className="text-sm text-[#6B7280] font-ibm mt-0.5">Your case, narrated by an AI legal representative — what happened and why it matters.</p>
        </div>
        {hasAny && (
          <button onClick={() => generate({ trigger: 'manual' })} disabled={generating} data-testid="narration-regenerate-button"
            className="flex items-center gap-2 px-3 py-2 border border-[#002FA7] text-[#002FA7] font-ibm text-sm font-semibold rounded-md hover:bg-[#002FA7]/5 transition-all disabled:opacity-50">
            {generating ? <><Spinner size={14} className="animate-spin" /> Building…</> : <><ArrowClockwise size={14} /> Regenerate</>}
          </button>
        )}
      </div>

      {isStale && !generating && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm font-ibm text-amber-800" data-testid="narration-stale-banner">
          <Warning size={16} /> The case has changed since this narration. A new version will reflect the latest documents and events.
          <button onClick={() => generate({ trigger: 'material_change' })} className="ml-auto underline font-semibold">Build updated version</button>
        </div>
      )}

      {!hasAny && !generating && (
        <div className="text-center py-14 bg-white border border-dashed border-[#E5E7EB] rounded-lg" data-testid="narration-empty">
          <Microphone size={44} className="mx-auto text-[#002FA7]/40 mb-3" />
          <p className="text-[#6B7280] font-ibm mb-4 max-w-md mx-auto">No narration yet. Generate a spoken walkthrough of your case in the voice of a legal representative — replayable anytime, and updated as your case evolves.</p>
          <button onClick={() => generate({ trigger: 'manual' })} disabled={generating} data-testid="narration-generate-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#002FA7] text-white font-ibm font-semibold rounded-md hover:bg-[#00237D] transition-all disabled:opacity-50">
            <Sparkle size={16} weight="fill" /> Generate Narration
          </button>
        </div>
      )}

      {showGeneratingState && (
        <div className="text-center py-14 bg-white border border-[#E5E7EB] rounded-lg" data-testid="narration-generating">
          <Spinner size={40} className="animate-spin text-[#002FA7] mx-auto mb-3" />
          <p className="text-[#09090B] font-ibm font-semibold">Preparing your narration…</p>
          <p className="text-[#6B7280] font-ibm text-sm mt-1">Reviewing the chronology, evidence and applicable laws, then recording the voice. This takes about a minute.</p>
        </div>
      )}

      {errored && (
        <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm font-ibm text-red-700" data-testid="narration-error">
          Narration failed: {active.error || 'unknown error'}. Please try Regenerate.
        </div>
      )}

      {active?.status === 'ready' && <NarrationPlayer active={active} audioUrl={audioUrl} />}

      {hasAny && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid="narration-feedback">
          <p className="text-sm font-ibm font-semibold text-[#09090B] mb-2">Fine-tune the narration</p>
          <p className="text-xs text-[#6B7280] font-ibm mb-2">Tell the AI how to adjust — e.g. "be more assertive", "focus on the timeline gaps", "keep it under 60 seconds", "explain the legal risk more plainly". A new version is created so you can compare.</p>
          <div className="flex gap-2">
            <input value={feedback} onChange={(e) => setFeedback(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && feedback.trim() && !generating) generate({ feedback: feedback.trim() }); }}
              placeholder="Your feedback…" data-testid="narration-feedback-input"
              className="flex-1 px-3 py-2 border border-[#E5E7EB] rounded-md text-sm font-ibm focus:outline-none focus:border-[#002FA7]" />
            <button onClick={() => generate({ feedback: feedback.trim() })} disabled={generating || !feedback.trim()} data-testid="narration-feedback-submit"
              className="flex items-center gap-1.5 px-4 py-2 bg-[#002FA7] text-white font-ibm text-sm font-semibold rounded-md hover:bg-[#00237D] transition-all disabled:opacity-50">
              {generating ? <><Spinner size={14} className="animate-spin" /> Refining…</> : <><PaperPlaneTilt size={14} /> Refine</>}
            </button>
          </div>
          {generating && active?.status === 'ready' && (
            <p className="text-xs text-[#002FA7] font-ibm mt-2 flex items-center gap-1.5" data-testid="narration-refining-note">
              <Spinner size={12} className="animate-spin" /> Building an updated narration — your current version stays playable until it's ready.
            </p>
          )}
        </div>
      )}

      <NarrationVersionList versions={status?.versions} activeId={active?._id} loadNarration={loadNarration} deleteVersion={deleteVersion} />

      <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 font-ibm">
        ⚠ This AI narration is informational, generated from your materials — not legal advice. Review it before relying on it.
      </p>
    </div>
  );
};
